<template>
  <div ref="chartContainer" class="optimal-hedge-chart">
    <div class="chart-header text-left">
      <div class="chart-subtitle">Optimal Hedge Ratio</div>
      <div class="chart-main-title">Forward</div>
    </div>

    <vue-apex-charts
      :key="chartKey"
      type="bar"
      height="180"
      :options="chartOptions"
      :series="series"
    />

    <div class="amounts-section">
      <div class="amounts-title">Amounts to Hedge</div>
      <div
        v-for="(item, i) in amounts"
        :key="item.label"
        class="amount-row"
        :class="{ 'animate-row': animated }"
        :style="{ animationDelay: (i * 80) + 'ms', backgroundColor: item.color + '12' }"
      >
        <span class="amount-dot" :style="{ backgroundColor: item.color }"></span>
        <span class="amount-label">{{ item.label }}</span>
        <span class="amount-value">{{ item.value }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const COLORS = ['#8B5CF6', '#F97316', '#22C55E', '#EAB308']

export default {
  name: 'OptimalHedgeChart',

  data () {
    return {
      chartKey: 0,
      animated: false,
      observer: null,

      amounts: [
        { label: 'BRL', color: COLORS[0], value: '€3.3m' },
        { label: 'Brent Oil', color: COLORS[1], value: '€7.16m' },
        { label: 'EURIBOR 3m', color: COLORS[2], value: '€30m' },
        { label: 'USD', color: COLORS[3], value: '€21.6m' }
      ],

      series: [
        { name: 'Hedge Ratio', data: [68, 78, 76, 86] }
      ],

      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          background: 'transparent',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 700,
            animateGradually: { enabled: true, delay: 100 },
            dynamicAnimation: { enabled: true, speed: 400 }
          }
        },
        colors: COLORS,
        plotOptions: {
          bar: {
            distributed: true,
            borderRadius: 0,
            columnWidth: '75%',
            dataLabels: { position: 'top' }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: val => val + '%',
          offsetY: -15,
          style: {
            fontSize: '10px',
            fontWeight: '700',
            colors: ['#1e293b']
          },
          dropShadow: { enabled: false }
        },
        legend: { show: false },
        xaxis: {
          categories: ['BRL', 'Brent Oil', 'EURIBOR 3m', 'USD'],
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false }
        },
        yaxis: {
          min: 0,
          max: 100,
          tickAmount: 4,
          labels: {
            formatter: val => val + '%',
            style: { fontSize: '10px', colors: '#94a3b8' }
          }
        },
        grid: {
          borderColor: '#e2e8f0',
          strokeDashArray: 5,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { top: 10, bottom: 0, left: 20, right: 0 }
        },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'lighten', value: 0.08 } }
        }
      }
    }
  },

  mounted () {
    this._setupObserver()
  },

  beforeDestroy () {
    if (this.observer) this.observer.disconnect()
  },

  methods: {
    _setupObserver () {
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animated = false
              this.$nextTick(() => {
                this.chartKey++
                this.animated = true
              })
            }
          })
        },
        { threshold: 0.3 }
      )
      if (this.$refs.chartContainer) {
        this.observer.observe(this.$refs.chartContainer)
      }
    }
  }
}
</script>

<style scoped>
.optimal-hedge-chart {
  max-width: 450px;
  overflow: hidden;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 16px 16px;
  text-align: left;
}

.chart-subtitle {
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.02em;
  margin-bottom: 2px;
}

.chart-main-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

/* Amounts section */

.amounts-title {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}

.amount-row {
  display: flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 5px;
  opacity: 0;
  margin-bottom: 2px;
}

.amount-row.animate-row {
  animation: fadeSlideUp 0.4s ease forwards;
}

.amount-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 10px;
}

.amount-label {
  flex: 1;
  font-size: 10px;
  color: #475569;
  font-weight: 400;
}

.amount-value {
  font-size: 10px;
  color: #1e293b;
  font-weight: 600;
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
